import type { IPhone } from '../components/base/input/Phone.vue'
import { FetchError } from 'ofetch';
import './Date';
import './Array';
import './Object';

declare global {
  // IRole structure
  interface IRole {
    name: String | string;
    permissions: Array<String | string>;
  }

  // IAPI structure
  interface IAPI {
    status: boolean;
    name: String | string | null;
    surname: String | string | null;
    permissions: Array<String | string> | null;
  }

  // IUser structure
  interface IUser {
    id: Number | number | String | string | null;
    name: String | string | null;
    surname: String | string | null;
    phone: IPhone;
    mobile: IPhone;
    email: String | string | null;
    created_at: Date | String | string | null;
    updated_at: Date | String | string | null;
    deleted_at: Date | String | string | null;
    password?: String | string | null | undefined;
    signatureCode?: Number | number | null | undefined;
    remember?: boolean | undefined;
    roles: Array<IRole>;
    notification_subscriptions: Array<String | string>;
    organization?: Object | null | undefined;
    api?: IAPI | null | undefined;
  }

  type IEUser = Omit<IUser, ('roles')>;
  // IAuth structure
  interface IAuth extends IEUser {
    login: (data?: IAuth) => Promise<any>;
    logged: () => boolean;
    logout: () => Promise<any>;
    refresh: () => Promise<any>;
    hasPermission: (...permissions: Array<String | string>) => boolean;
    hasApiPermission: (...permissions: Array<String | string>) => boolean;
    hasRole: (...roles: Array<String | string>) => boolean;
    routeAvailable: (to: { name: String | string }, api: boolean) => boolean;
    fetchProfile: () => Promise<any>;
    roles: Array<String | string>;
    permissions: Array<String | string>;
  }

  // class Auth implements IAuth {
  //   id: Number | String | string | null;
  //   name: String | string | null;
  //   surname: String | string | null;
  //   phone: IPhone;
  //   mobile: IPhone;
  //   email: String | string | null;
  //   created_at: Date | String | string | null;
  //   updated_at: Date | String | string | null;
  //   deleted_at: Date | String | string | null;
  //   roles: Array<String>;
  //   permissions: Array<String>;
  //   organization: Object | null | undefined;

  //   // Instance check bcs. typescript
  //   private static instanceOfUser(object: any): object is IUser;

  //   // Init
  //   private init(object?: IUser | null): void;

  //   // C'tor
  //   public constructor(object?: IUser | null);

  //   // Logged is authenticated
  //   public logged(): boolean;

  //   // Login method
  //   public login(data?: IAuth): Promise<any>;

  //   // Logout method
  //   public logout(): Promise<any>;

  //   // Refresh method
  //   public refresh(): Promise<any>;

  //   // Permission check method
  //   public hasPermission(...permissions: Array<String | string>): boolean;

  //   // Role check method
  //   public hasRole(...roles: Array<String | string>): boolean;

  //   // Permission check method
  //   public routeAvailable(to: { name: String | string }, api: boolean): boolean;

  //   // Fetch profile
  //   public fetchProfile(): Promise<any>;
  // }
}

export class Auth implements IAuth {
  id: Number | number | String | string | null = null;
  name: String | string | null = null;
  surname: String | string | null = null;
  phone = <IPhone>{
    prefix: null,
    number: null,
  };
  mobile = <IPhone>{
    prefix: null,
    number: null,
  };
  email: String | string | null = null;
  created_at: Date | String | string | null = null;
  updated_at: Date | String | string | null = null;
  deleted_at: Date | String | string | null = null;
  roles: Array<String | string> = [];
  permissions: Array<String | string> = [];
  notification_subscriptions: Array<String | string> = [];
  organization: Object | null = null;
  api: IAPI | null = null;

  // Instance check bcs. typescript
  private static instanceOfUser(object: any): object is IUser {
    if (object)
      return (
        'id' in object &&
        'name' in object &&
        'surname' in object &&
        'phone' in object &&
        'mobile' in object &&
        'email' in object &&
        'roles' in object &&
        'created_at' in object &&
        'updated_at' in object &&
        'deleted_at' in object
      );
    return false;
  }

  // Init
  private init(object?: IUser | null): void {
    if (Auth.instanceOfUser(object)) {
      this.id = object.id;
      this.name = object.name;
      this.surname = object.surname;
      this.phone = <IPhone>{
        prefix: object.phone?.prefix,
        number: object.phone?.number,
      };
      this.mobile = <IPhone>{
        prefix: object.mobile?.prefix,
        number: object.mobile?.number,
      };
      this.email = object.email;
      this.created_at = Date.parseData(object.created_at);
      this.updated_at = Date.parseData(object.updated_at);
      this.deleted_at = Date.parseData(object.deleted_at);
      this.roles = [];
      this.permissions = [];
      if (Array.isArray(object.roles)) {
        this.roles = object.roles.pluck('name');
        object.roles?.forEach((role: IRole) =>
          ((Array.isArray(role?.permissions)) ? (this.permissions = this.permissions.union(role.permissions)) : null));
      }
      this.permissions = this.permissions.unique();
      this.api = (object.api ?? null);
      this.organization = (object.organization ?? null);
      this.notification_subscriptions = object.notification_subscriptions;
    }
  }

  // C'tor
  public constructor(object?: IUser | null) {
    this.init(object);
  }

  // Logged is authenticated
  public logged(): boolean {
    return useSanctumAuth()?.isAuthenticated?.value;
  }

  // Login method
  public async login(data?: IAuth): Promise<any> {
    let auth = {};
    if (data)
      auth = data;
    else
      auth = this;
    if (Auth.instanceOfUser(auth) && !this.logged()) {
      try {
        auth = JSON.parse(JSON.stringify(auth)); // CANNOT SEND CLASS VIA API ONLY OBJECTS
        return await useSanctumAuth()?.login(auth);
      } catch (e) {
        if (e instanceof FetchError && (e as FetchError).response?.status === 422)
          console.log((e as FetchError).response?._data?.errors);
        return e;
      }
    }
  }

  // Logout method
  public async logout(): Promise<any> {
    if (this.logged())
      try {
        clearNuxtData();
        return await useSanctumAuth()?.logout();
      } catch (e) {
        if (e instanceof FetchError && (e as FetchError).response?.status === 422)
          console.log((e as FetchError).response?._data?.errors);
        return e;
      }
  }

  // Refresh method
  public async refresh(): Promise<any> {
    try {
      return await useSanctumAuth()?.refreshIdentity();
    } catch (e) {
      if (e instanceof FetchError && (e as FetchError).response?.status === 422)
        console.log((e as FetchError).response?._data?.errors);
      return e;
    }
  }

  // Permissions check method
  public permissionCheck(localPermissions: Array<String | string>, ...permissions: Array<String | string>): boolean {
    if (Array.isArray(permissions) && Array.isArray(localPermissions)) {
      permissions = (permissions?.map((permission: String | string) => permission?.replace(/[.+^${}()|[\]\\]/g, '\\$&')?.replace(/\*/g, '.*')) ?? []);
      const pattern = new RegExp(`^(?:${permissions?.join('|')})$`);
      return localPermissions?.some((permission: String | string) => pattern.test((permission as string)));
    }
    return false;
  }

  // Permission check method
  public hasPermission(...permissions: Array<String | string>): boolean {
    return this.permissionCheck(this.permissions, ...permissions);
  }

  // Permission check method
  public hasApiPermission(...permissions: Array<String | string>): boolean {
    if (this.api?.status ? Array.isArray(this.api?.permissions) : false)
      return this.permissionCheck(this.api?.permissions as Array<String | string>, ...permissions);
    return false;
  }

  // Role check method
  public hasRole(...roles: Array<String | string>): boolean {
    if (Array.isArray(roles) && Array.isArray(this.roles)) {
      roles = (roles?.map((role: String | string) => role?.replace(/[.+^${}()|[\]\\]/g, '\\$&')?.replace(/\*/g, '.*')) ?? []);
      const pattern = new RegExp(`^(?:${roles?.join('|')})$`);
      return this.roles?.some((role: String | string) => pattern.test((role as string)));
    }
    return false;
  }

  // Permission check method
  public routeAvailable(to: { name: String | string }, api: boolean = false): boolean {
    let routePermission = to.name?.split('-') as Array<String | string>;
    switch (routePermission[routePermission.length - 1]) {
      case 'archive':
        routePermission[routePermission.length - 1] = 'archive';
        break;
      case 'delete':
        routePermission[routePermission.length - 1] = 'delete';
        break;
      case 'edit':
      case 'active':
        routePermission[routePermission.length - 1] = 'update';
        break;
      case 'clone':
      case 'create':
        routePermission[routePermission.length - 1] = 'create';
        break;
      default:
        routePermission.push('read');
        break;
    }
    routePermission = routePermission.filter((n: String | string) => (n !== 'id'));
    // Permission direct exist
    if (api) {
      if (this.hasApiPermission(routePermission.join('-')))
        return true;
    } else if (this.hasPermission(routePermission.join('-')))
      return true;
    // Permissions cascading exist
    if (Array.isArray(this.permissions)) {
      let permissionsCascade = [] as Array<String | string>;
      switch (routePermission.pop()) {
        case 'archive':
        case 'active':
        case 'read':
          permissionsCascade.push(routePermission.join('-') + '-read');
        case 'clone':
        case 'create':
          permissionsCascade.push(routePermission.join('-') + '-create');
        case 'update':
          permissionsCascade.push(routePermission.join('-') + '-update');
        case 'delete':
          permissionsCascade.push(routePermission.join('-') + '-delete');
      }
      if (api) {
        if (this.hasApiPermission(...permissionsCascade))
          return true;
      } else if (this.hasPermission(...permissionsCascade))
        return true;
    }
    return false;
  }

  // Fetch profile
  public async fetchProfile(): Promise<any> {
    const client = useSanctumClient();
    return await client(useSanctumConfig()?.endpoints?.user, {
      method: 'GET'
    });
  }
}
